import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { BiArrowBack, BiPlus, BiSearch } from "react-icons/bi";
import { Card } from "@material-tailwind/react";

import { Colors } from "../../../assets/Colors";
import { fetchDelete, fetchGet, fetchPost, fetchPut } from "../../../fetchers";

/**
 * Master Struktur Organisasi (card HRD di /master) — disusun PER BAGIAN.
 *
 * Tiap Bagian (Master Bagian) punya susunan jabatannya sendiri. Jabatan DIAMBIL dari
 * Master Jabatan (bukan teks bebas). Jabatan tanpa bagian = level "Pimpinan
 * Perusahaan" (Direktur dsb.); kepala tiap bagian boleh beratasan salah satu
 * Pimpinan sehingga bagan seluruh perusahaan tersambung. Aturan atasan dijaga
 * backend (models/master/StrukturOrganisasi.go cekAtasan):
 *   - Pimpinan → atasan hanya sesama Pimpinan;
 *   - jabatan bagian → atasan di bagian yang sama, atau Pimpinan (kepala bagian).
 * Pemegang jabatan = karyawan aktif dengan Jabatan (+ Bagian) yang sama.
 */

const PIMPINAN = "__PIMPINAN__";
const SEMUA = "__SEMUA__";

const KOSONG = {
  StrukturID: "",
  JabatanIDs: [],
  JabatanID: "",
  BagianID: "",
  ParentID: "",
  Keterangan: "",
  SortOrder: 0,
  IsActive: true,
  SampaiDirektur: false,
};

const labelNode = (n) =>
  n ? n.NamaJabatan + (n.NamaBagian ? ` — ${n.NamaBagian}` : " (Pimpinan)") : "";

// Semua bawahan (langsung & tak langsung) — tak boleh dipilih jadi atasan saat ubah.
const kumpulkanBawahan = (id, anakDari) => {
  const hasil = new Set();
  const tumpuk = [id];
  while (tumpuk.length) {
    const cur = tumpuk.pop();
    (anakDari[cur] || []).forEach((c) => {
      if (!hasil.has(c.StrukturID)) {
        hasil.add(c.StrukturID);
        tumpuk.push(c.StrukturID);
      }
    });
  }
  return hasil;
};

// Atasan yang sah untuk node berbagian `bagianID` ("" = Pimpinan) — cermin cekAtasan backend.
const atasanSah = (calon, bagianID) =>
  !calon.BagianID || (bagianID !== "" && calon.BagianID === bagianID);

const ModalNode = ({ show, mode, data, kunciBagian, nodes, anakDari, opsi, saranJabatan, busy, onSimpan, onBatal }) => {
  const [form, setForm] = useState(KOSONG);

  useEffect(() => {
    if (show) setForm({ ...KOSONG, ...data });
  }, [show, data]);

  const isEdit = mode === "edit";
  const terlarang = useMemo(
    () => (isEdit && form.StrukturID ? kumpulkanBawahan(form.StrukturID, anakDari) : new Set()),
    [isEdit, form.StrukturID, anakDari]
  );

  if (!show) return null;

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const bagian = form.BagianID || "";
  const opsiAtasan = [
    { value: "", label: bagian ? "— Tanpa atasan (puncak bagian) —" : "— Tanpa atasan (puncak perusahaan) —" },
    ...nodes
      .filter(
        (n) => n.StrukturID !== form.StrukturID && !terlarang.has(n.StrukturID) && atasanSah(n, bagian)
      )
      // Atasan dari bagian sendiri dulu, lalu Pimpinan.
      .sort((a, b) => (a.BagianID ? 0 : 1) - (b.BagianID ? 0 : 1))
      .map((n) => ({ value: n.StrukturID, label: n.BagianID ? n.NamaJabatan : `[Pimpinan] ${n.NamaJabatan}` })),
  ];
  const opsiBagian = [{ value: "", label: "— Pimpinan Perusahaan (tanpa bagian) —" }, ...opsi.Bagian];

  // Jabatan yang dipakai karyawan di bagian ini tampil paling atas beserta jumlahnya.
  const saran = saranJabatan(bagian);
  const opsiJabatan = (() => {
    if (!saran.length) return opsi.Jabatan;
    const ada = new Set(saran.map((x) => x.JabatanID));
    return [
      {
        label: "Dipakai karyawan di bagian ini",
        options: saran.map((x) => ({ value: x.JabatanID, label: `${x.NamaJabatan} (${x.Jumlah} karyawan)` })),
      },
      { label: "Jabatan lain", options: opsi.Jabatan.filter((o) => !ada.has(o.value)) },
    ];
  })();
  const semuaOpsiJabatan = opsiJabatan[0]?.options ? opsiJabatan.flatMap((g) => g.options) : opsiJabatan;

  const takValid = isEdit ? !form.JabatanID : form.JabatanIDs.length === 0;
  const atasanTakSah = form.ParentID && !opsiAtasan.some((o) => o.value === form.ParentID);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 overflow-y-auto py-8">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg mx-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          {isEdit ? "Ubah Jabatan di Struktur" : "Tambah Jabatan ke Struktur"}
        </h3>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Bagian</label>
            <Select
              options={opsiBagian}
              isDisabled={kunciBagian}
              value={opsiBagian.find((o) => o.value === bagian) || opsiBagian[0]}
              onChange={(o) => setForm((p) => ({ ...p, BagianID: o ? o.value : "", ParentID: "" }))}
            />
            {kunciBagian && (
              <p className="text-xs text-gray-500 mt-1">Mengikuti bagian yang sedang dibuka.</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Jabatan (dari Master Jabatan) <span className="text-red-500">*</span>
            </label>
            {isEdit ? (
              <Select
                options={opsiJabatan}
                value={semuaOpsiJabatan.find((o) => o.value === form.JabatanID) || null}
                onChange={(o) => set("JabatanID", o ? o.value : "")}
                placeholder="Pilih jabatan…"
              />
            ) : (
              <>
                <Select
                  isMulti
                  options={opsiJabatan}
                  value={semuaOpsiJabatan.filter((o) => form.JabatanIDs.includes(o.value))}
                  onChange={(arr) => set("JabatanIDs", (arr || []).map((o) => o.value))}
                  placeholder="Pilih satu atau beberapa jabatan…"
                  closeMenuOnSelect={false}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Beberapa jabatan sekaligus dibuat sebagai kotak terpisah dengan atasan yang sama.
                </p>
              </>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Atasan</label>
            <Select
              options={opsiAtasan}
              value={opsiAtasan.find((o) => o.value === (form.ParentID || "")) || null}
              onChange={(o) => set("ParentID", o ? o.value : "")}
              placeholder="Pilih atasan…"
            />
            <p className="text-xs text-gray-500 mt-1">
              {bagian
                ? "Atasan dari bagian yang sama, atau Pimpinan untuk kepala bagian."
                : "Level Pimpinan hanya bisa beratasan sesama Pimpinan."}
            </p>
            {atasanTakSah && (
              <p className="text-xs text-red-600 mt-1">Atasan saat ini tidak sah untuk bagian ini — pilih ulang.</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Keterangan</label>
            <input
              type="text"
              value={form.Keterangan}
              maxLength={500}
              onChange={(e) => set("Keterangan", e.target.value)}
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>

          {isEdit && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Urutan (kiri → kanan)</label>
                <input
                  type="number"
                  value={form.SortOrder}
                  onChange={(e) => set("SortOrder", parseInt(e.target.value || "0", 10))}
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={!!form.IsActive}
                    onChange={(e) => set("IsActive", e.target.checked)}
                  />
                  Aktif
                </label>
              </div>
              <div className="col-span-2">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={!!form.SampaiDirektur}
                    onChange={(e) => set("SampaiDirektur", e.target.checked)}
                  />
                  Approval Cuti/Lembur/Sakit lanjut sampai Direktur
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Tanpa tanda: karyawan di jabatan ini berhenti di Pimpinan pertama (Base Manager). Beri tanda untuk
                  Supervisor/Analys/Manager agar lanjut satu tingkat Pimpinan lagi.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-3 justify-end mt-6">
          <button
            onClick={onBatal}
            disabled={busy}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50"
          >
            Batal
          </button>
          <button
            onClick={() => onSimpan(form)}
            disabled={takValid || atasanTakSah || busy}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? "Menyimpan…" : "Simpan"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Satu kotak + bawahannya (rekursif) di dalam lingkup yang sedang dibuka.
const KotakNode = ({ node, anakDari, dalamLingkup, byId, lingkup, cari, onTambah, onUbah, onHapus, bolehUbah }) => {
  const anakSemua = anakDari[node.StrukturID] || [];
  const anak = anakSemua.filter(dalamLingkup);
  const atasanLuar = node.ParentID && byId[node.ParentID] && !dalamLingkup(byId[node.ParentID]) ? byId[node.ParentID] : null;
  const cocok =
    cari &&
    (labelNode(node).toLowerCase().includes(cari) ||
      node.Pemegang.some((p) => p.NamaKaryawan.toLowerCase().includes(cari)));
  const pimpinan = !node.BagianID;
  return (
    <li>
      <div
        className={`inline-block text-left bg-white border rounded-lg shadow-sm px-3 py-2 w-56 align-top ${
          node.IsActive ? (pimpinan ? "border-blue-400" : "border-gray-300") : "border-dashed border-gray-300 opacity-60"
        } ${cocok ? "ring-2 ring-yellow-400" : ""}`}
        style={pimpinan ? { borderTopWidth: 3 } : undefined}
      >
        {atasanLuar && (
          <div className="text-[11px] text-gray-500 mb-1">↑ melapor ke {atasanLuar.NamaJabatan}</div>
        )}
        <div className="font-semibold text-sm text-gray-800 leading-tight">{node.NamaJabatan}</div>
        {lingkup === SEMUA && (
          <div className={`text-xs ${pimpinan ? "text-blue-700" : "text-gray-500"}`}>
            {pimpinan ? "Pimpinan Perusahaan" : node.NamaBagian}
          </div>
        )}
        <div className="mt-1 text-xs text-gray-600">
          {node.Pemegang.length === 0 ? (
            <span className="italic text-gray-400">Belum ada pemegang</span>
          ) : (
            <>
              {node.Pemegang.slice(0, 3).map((p) => (
                <div key={p.Nik} className="truncate" title={`${p.NamaKaryawan} (${p.Nik}) — ${p.NamaBagian}`}>
                  • {p.NamaKaryawan}
                </div>
              ))}
              {node.Pemegang.length > 3 && (
                <div className="text-gray-400" title={node.Pemegang.slice(3).map((p) => p.NamaKaryawan).join(", ")}>
                  +{node.Pemegang.length - 3} lainnya
                </div>
              )}
            </>
          )}
        </div>
        {node.Keterangan && <div className="mt-1 text-xs text-gray-500 italic">{node.Keterangan}</div>}
        {!node.IsActive && <div className="mt-1 text-xs text-gray-500">(Nonaktif)</div>}
        {node.SampaiDirektur && <div className="mt-1 text-[11px] text-orange-600">Approval lanjut ke Direktur</div>}
        {lingkup === PIMPINAN && anakSemua.length > anak.length && (
          <div className="mt-1 text-[11px] text-gray-500">
            Kepala bagian: {anakSemua.filter((c) => !dalamLingkup(c)).map((c) => c.NamaBagian).join(", ")}
          </div>
        )}
        {bolehUbah && (
          <div className="mt-2 flex gap-1">
            <button
              onClick={() => onTambah(node)}
              className="text-xs border border-blue-600 text-blue-600 hover:bg-blue-50 rounded px-2 py-0.5"
              title="Tambah bawahan"
            >
              + Bawahan
            </button>
            <button
              onClick={() => onUbah(node)}
              className="text-xs border border-gray-400 text-gray-700 hover:bg-gray-50 rounded px-2 py-0.5"
            >
              Ubah
            </button>
            <button
              onClick={() => onHapus(node, anakSemua.length)}
              className="text-xs border border-red-500 text-red-600 hover:bg-red-50 rounded px-2 py-0.5"
            >
              Hapus
            </button>
          </div>
        )}
      </div>
      {anak.length > 0 && (
        <ul>
          {anak.map((c) => (
            <KotakNode
              key={c.StrukturID}
              node={c}
              anakDari={anakDari}
              dalamLingkup={dalamLingkup}
              byId={byId}
              lingkup={lingkup}
              cari={cari}
              onTambah={onTambah}
              onUbah={onUbah}
              onHapus={onHapus}
              bolehUbah={bolehUbah}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

// Garis bagan organisasi (pohon ke bawah) dengan CSS murni.
const CSS_BAGAN = `
.so-tree ul { padding-top: 20px; position: relative; display: flex; justify-content: center; }
.so-tree li { list-style: none; text-align: center; position: relative; padding: 20px 6px 0 6px; }
.so-tree li::before, .so-tree li::after {
  content: ''; position: absolute; top: 0; right: 50%; width: 50%; height: 20px; border-top: 1px solid #9ca3af;
}
.so-tree li::after { right: auto; left: 50%; border-left: 1px solid #9ca3af; }
.so-tree li:only-child::after, .so-tree li:only-child::before { display: none; }
.so-tree li:only-child { padding-top: 0; }
.so-tree li:first-child::before, .so-tree li:last-child::after { border: 0 none; }
.so-tree li:last-child::before { border-right: 1px solid #9ca3af; border-radius: 0 5px 0 0; }
.so-tree li:first-child::after { border-radius: 5px 0 0 0; }
.so-tree ul ul::before {
  content: ''; position: absolute; top: 0; left: 50%; border-left: 1px solid #9ca3af; width: 0; height: 20px;
}
.so-tree > ul { padding-top: 0; }
`;

const StrukturOrganisasiPage = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  // Otoritas sendiri SOtoritas.MStrukturOrganisasi (Setup Otoritas): 1 = lihat, 2 = ubah.
  const bolehUbah = (user?.Otoritas?.MStrukturOrganisasi ?? 0) >= 2;

  const [data, setData] = useState({ Nodes: [], Jabatan: [], Bagian: [], JabatanBagian: [] });
  const [loading, setLoading] = useState(true);
  const [lingkup, setLingkup] = useState(SEMUA);
  const [cari, setCari] = useState("");
  const [tampil, setTampil] = useState("bagan");
  const [modal, setModal] = useState({ show: false, mode: "add", data: KOSONG, kunciBagian: false });
  const [busy, setBusy] = useState(false);

  const muat = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchGet("Master/StrukturOrganisasi");
      setData({ Nodes: [], Jabatan: [], Bagian: [], JabatanBagian: [], ...(res?.data ?? {}) });
    } catch (_) {
      // Kesalahan jaringan/server — halaman tetap tampil kosong.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    muat();
  }, [muat]);

  const { anakDari, byId } = useMemo(() => {
    const byId = {};
    data.Nodes.forEach((n) => (byId[n.StrukturID] = n));
    const anakDari = {};
    data.Nodes.forEach((n) => {
      if (n.ParentID && byId[n.ParentID]) (anakDari[n.ParentID] = anakDari[n.ParentID] || []).push(n);
    });
    return { anakDari, byId };
  }, [data.Nodes]);

  const dalamLingkup = useCallback(
    (n) => (lingkup === SEMUA ? true : lingkup === PIMPINAN ? !n.BagianID : n.BagianID === lingkup),
    [lingkup]
  );
  const nodesLingkup = data.Nodes.filter(dalamLingkup);
  // Akar dalam lingkup = node yang atasannya di luar lingkup (atau tanpa atasan).
  const akar = nodesLingkup.filter((n) => !n.ParentID || !byId[n.ParentID] || !dalamLingkup(byId[n.ParentID]));

  const jumlahPerBagian = useMemo(() => {
    const m = {};
    data.Nodes.forEach((n) => (m[n.BagianID || PIMPINAN] = (m[n.BagianID || PIMPINAN] || 0) + 1));
    return m;
  }, [data.Nodes]);

  // Jabatan dipakai karyawan aktif di bagian `b` (tanpa yang sudah ada di struktur bagian itu ditandai).
  const saranJabatan = useCallback(
    (b) => (b ? data.JabatanBagian.filter((x) => x.BagianID === b) : []),
    [data.JabatanBagian]
  );
  const belumDitempatkan =
    lingkup !== SEMUA && lingkup !== PIMPINAN
      ? saranJabatan(lingkup).filter(
          (x) => !data.Nodes.some((n) => n.BagianID === lingkup && n.JabatanID === x.JabatanID)
        )
      : [];

  const namaLingkup =
    lingkup === SEMUA
      ? "Seluruh Perusahaan"
      : lingkup === PIMPINAN
      ? "Pimpinan Perusahaan"
      : data.Bagian.find((b) => b.value === lingkup)?.label || lingkup;

  const simpan = async (form) => {
    setBusy(true);
    try {
      if (modal.mode === "edit") {
        await fetchPut("Master/StrukturOrganisasi", {
          StrukturID: form.StrukturID,
          JabatanID: form.JabatanID,
          BagianID: form.BagianID || "",
          ParentID: form.ParentID || "",
          Keterangan: (form.Keterangan || "").trim(),
          SortOrder: Number(form.SortOrder) || 0,
          IsActive: !!form.IsActive,
          SampaiDirektur: !!form.SampaiDirektur,
        });
      } else {
        await fetchPost("Master/StrukturOrganisasi", {
          JabatanIDs: form.JabatanIDs,
          BagianID: form.BagianID || "",
          ParentID: form.ParentID || "",
          Keterangan: (form.Keterangan || "").trim(),
        });
      }
      setModal({ show: false, mode: "add", data: KOSONG, kunciBagian: false });
      muat();
    } catch (_) {
      // Modal dibiarkan terbuka; fetchPost/fetchPut sudah menampilkan pesan server.
    } finally {
      setBusy(false);
    }
  };

  const hapus = async (node, jumlahAnak) => {
    if (jumlahAnak > 0) {
      window.alert(
        `"${labelNode(node)}" masih punya ${jumlahAnak} bawahan. Pindahkan (Ubah → Atasan) atau hapus bawahannya dulu.`
      );
      return;
    }
    if (!window.confirm(`Hapus "${labelNode(node)}" dari struktur organisasi?`)) return;
    try {
      await fetchDelete("Master/StrukturOrganisasi", { StrukturID: node.StrukturID });
      muat();
    } catch (_) {}
  };

  const bagianLingkup = lingkup === SEMUA ? null : lingkup === PIMPINAN ? "" : lingkup;

  // Tambah dari tombol atas (atasan = null) atau "+ Bawahan" (atasan = node).
  const bukaTambah = (atasan, jabatanIDs = []) => {
    let bagianBaru;
    if (bagianLingkup !== null) bagianBaru = bagianLingkup;
    else bagianBaru = atasan?.BagianID || "";
    setModal({
      show: true,
      mode: "add",
      // Di tampilan per bagian / pimpinan, bagian dikunci ke lingkup yang dibuka.
      kunciBagian: bagianLingkup !== null,
      data: { ...KOSONG, BagianID: bagianBaru, ParentID: atasan ? atasan.StrukturID : "", JabatanIDs: jabatanIDs },
    });
  };
  const bukaUbah = (n) => setModal({ show: true, mode: "edit", kunciBagian: false, data: { ...KOSONG, ...n } });

  const q = cari.trim().toLowerCase();
  const barisTabel = nodesLingkup.filter(
    (n) =>
      !q ||
      labelNode(n).toLowerCase().includes(q) ||
      n.Pemegang.some((p) => p.NamaKaryawan.toLowerCase().includes(q))
  );

  const pilihanLingkup = [
    { value: SEMUA, label: "Seluruh Perusahaan", n: data.Nodes.length },
    { value: PIMPINAN, label: "Pimpinan Perusahaan", n: jumlahPerBagian[PIMPINAN] || 0 },
    ...data.Bagian.map((b) => ({ value: b.value, label: b.label, n: jumlahPerBagian[b.value] || 0 })),
  ];

  return (
    <div className="md:ml-64">
      <style>{CSS_BAGAN}</style>
      <ModalNode
        show={modal.show}
        mode={modal.mode}
        data={modal.data}
        kunciBagian={modal.kunciBagian}
        nodes={data.Nodes}
        anakDari={anakDari}
        opsi={data}
        saranJabatan={saranJabatan}
        busy={busy}
        onSimpan={simpan}
        onBatal={() => setModal({ show: false, mode: "add", data: KOSONG, kunciBagian: false })}
      />

      <div style={{ background: Colors.primary }} className="pt-14 pb-16 px-3 md:px-8 h-auto">
        <div className="container mx-auto max-w-full" />
      </div>

      <div className="px-3 md:px-8 h-auto -mt-32">
        <div className="container mx-auto max-w-full">
          <Card>
            <div className="px-6 py-4 border-b flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate("/master")}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm hover:bg-gray-100"
                  title="Kembali ke Master"
                >
                  <BiArrowBack />
                </button>
                <div>
                  <h2 className="text-lg font-semibold text-gray-800">Struktur Organisasi</h2>
                  <p className="text-xs text-gray-500">
                    Disusun per Bagian dari Master Jabatan. Pemegang jabatan diambil otomatis dari data Karyawan aktif.
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm">
                  {[
                    ["bagan", "Bagan"],
                    ["daftar", "Daftar"],
                  ].map(([v, l]) => (
                    <button
                      key={v}
                      onClick={() => setTampil(v)}
                      className={`px-3 py-2 ${tampil === v ? "bg-blue-600 text-white" : "bg-white hover:bg-gray-100"}`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
                <div className="relative">
                  <BiSearch className="absolute left-2 top-2.5 text-gray-400" />
                  <input
                    value={cari}
                    onChange={(e) => setCari(e.target.value)}
                    placeholder="Cari jabatan / nama"
                    className="pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg w-52"
                  />
                </div>
                {bolehUbah && (
                  <button
                    onClick={() => bukaTambah(null)}
                    className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
                  >
                    <BiPlus /> Tambah Jabatan
                  </button>
                )}
              </div>
            </div>

            <div className="flex flex-col md:flex-row">
              {/* Pilihan lingkup: seluruh perusahaan / pimpinan / per bagian */}
              <div className="md:w-64 shrink-0 border-b md:border-b-0 md:border-r">
                <div className="px-4 pt-3 pb-1 text-xs font-semibold text-gray-500 uppercase">Bagian</div>
                <div className="flex md:flex-col overflow-x-auto md:overflow-visible pb-2">
                  {pilihanLingkup.map((o) => (
                    <button
                      key={o.value}
                      onClick={() => setLingkup(o.value)}
                      className={`text-left text-sm px-4 py-2 whitespace-nowrap md:whitespace-normal flex justify-between gap-2 ${
                        lingkup === o.value ? "bg-blue-50 text-blue-700 font-semibold border-l-4 border-blue-600" : "hover:bg-gray-50 text-gray-700"
                      }`}
                    >
                      <span>{o.label}</span>
                      <span className="text-xs text-gray-400">{o.n}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="px-6 pt-4 text-sm font-semibold text-gray-700">{namaLingkup}</div>

                {belumDitempatkan.length > 0 && bolehUbah && (
                  <div className="mx-6 mt-3 text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-800">
                    Jabatan karyawan di bagian ini yang belum ada di struktur:{" "}
                    {belumDitempatkan.map((x) => `${x.NamaJabatan} (${x.Jumlah})`).join(", ")}.{" "}
                    <button
                      className="underline font-semibold"
                      onClick={() => bukaTambah(null, belumDitempatkan.map((x) => x.JabatanID))}
                    >
                      Tambahkan
                    </button>
                  </div>
                )}

                {loading ? (
                  <div className="px-6 py-10 text-center text-gray-400">Memuat…</div>
                ) : nodesLingkup.length === 0 ? (
                  <div className="px-6 py-10 text-center text-gray-500">
                    <p>Struktur {namaLingkup} belum disusun.</p>
                    {bolehUbah && (
                      <p className="text-sm mt-1">
                        {lingkup === SEMUA
                          ? "Mulai dari Pimpinan Perusahaan (mis. Direktur Utama), lalu susun tiap bagian."
                          : "Klik Tambah Jabatan untuk jabatan teratas, lalu + Bawahan di tiap kotak."}
                      </p>
                    )}
                  </div>
                ) : tampil === "bagan" ? (
                  <div className="overflow-auto px-4 py-6">
                    <div className="so-tree inline-block min-w-full">
                      <ul>
                        {akar.map((n) => (
                          <KotakNode
                            key={n.StrukturID}
                            node={n}
                            anakDari={anakDari}
                            dalamLingkup={dalamLingkup}
                            byId={byId}
                            lingkup={lingkup}
                            cari={q}
                            onTambah={bukaTambah}
                            onUbah={bukaUbah}
                            onHapus={hapus}
                            bolehUbah={bolehUbah}
                          />
                        ))}
                      </ul>
                    </div>
                  </div>
                ) : (
                  <div className="overflow-x-auto mt-3">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase">
                        <tr>
                          <th className="px-4 py-3 text-left">Jabatan</th>
                          <th className="px-4 py-3 text-left">Bagian</th>
                          <th className="px-4 py-3 text-left">Atasan</th>
                          <th className="px-4 py-3 text-left">Pemegang</th>
                          <th className="px-4 py-3 text-right">Bawahan</th>
                          <th className="px-4 py-3 text-left">Status</th>
                          {bolehUbah && <th className="px-4 py-3 text-left">Aksi</th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {barisTabel.map((n) => (
                          <tr key={n.StrukturID} className={n.IsActive ? "" : "bg-gray-50 text-gray-500"}>
                            <td className="px-4 py-3 font-medium">{n.NamaJabatan}</td>
                            <td className="px-4 py-3">{n.NamaBagian || "Pimpinan Perusahaan"}</td>
                            <td className="px-4 py-3">{n.ParentID ? labelNode(byId[n.ParentID]) : "— (puncak)"}</td>
                            <td className="px-4 py-3">
                              {n.Pemegang.length ? n.Pemegang.map((p) => p.NamaKaryawan).join(", ") : "-"}
                            </td>
                            <td className="px-4 py-3 text-right">{(anakDari[n.StrukturID] || []).length}</td>
                            <td className="px-4 py-3">{n.IsActive ? "Aktif" : "Nonaktif"}</td>
                            {bolehUbah && (
                              <td className="px-4 py-3 whitespace-nowrap">
                                <button
                                  onClick={() => bukaUbah(n)}
                                  className="border border-blue-600 text-blue-600 hover:bg-blue-50 rounded-lg px-3 py-1 text-xs font-medium mr-1"
                                >
                                  Ubah
                                </button>
                                <button
                                  onClick={() => hapus(n, (anakDari[n.StrukturID] || []).length)}
                                  className="border border-red-500 text-red-600 hover:bg-red-50 rounded-lg px-3 py-1 text-xs font-medium"
                                >
                                  Hapus
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 py-4 text-xs text-gray-500 border-t">
              Jabatan tanpa bagian = level Pimpinan Perusahaan. Kepala tiap bagian bisa beratasan salah satu Pimpinan
              agar tersambung di bagan Seluruh Perusahaan. Jabatan baru ditambahkan lewat Master Jabatan.
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default StrukturOrganisasiPage;
